import { Helmet } from 'react-helmet-async';
import { Fragment, type ReactNode } from 'react';

const PRIVACY_POLICY_TEXT = `## INTRODUÇÃO

Esta Política de Privacidade ("Política") descreve como a ARAUJO & SILVA LTDA, pessoa jurídica de direito privado, inscrita no CNPJ sob o nº 65.321.886/0001-10, com sede na Rua Pais Leme, nº 215, Conj. 1713, Pinheiros, São Paulo/SP, CEP 05.424-150, nome fantasia PLUMAA (doravante "Plumaa"), coleta, utiliza, armazena, compartilha e protege dados pessoais no âmbito de sua plataforma digital de intermediação em saúde.

Esta Política observa integralmente a Lei Geral de Proteção de Dados Pessoais (Lei nº 13.709/2018 — LGPD), o Marco Civil da Internet (Lei nº 12.965/2014), o Código de Defesa do Consumidor (Lei nº 8.078/1990) e demais normas aplicáveis.

Esta Política é parte integrante dos seguintes instrumentos contratuais, conforme o perfil do titular:

- Termos e Condições Gerais de Uso — Usuário;
- Termos e Condições de Uso — Colaborador;
- Termos de Credenciamento do Profissional da Saúde;
- Termos de Prestação de Serviços — Empresa Contratante.

## 1. GLOSSÁRIO

Dado Pessoal: informação relacionada a pessoa natural identificada ou identificável (art. 5º, I, da LGPD).

Dado Pessoal Sensível: dado pessoal sobre origem racial ou étnica, convicção religiosa, opinião política, filiação sindical, dado referente à saúde ou à vida sexual, dado genético ou biométrico (art. 5º, II).

Titular: pessoa natural a quem se referem os dados pessoais (art. 5º, V).

Controlador: a quem competem as decisões referentes ao tratamento de dados (art. 5º, VI).

Operador: quem realiza o tratamento de dados em nome do controlador (art. 5º, VII).

Encarregado (DPO): pessoa indicada pelo controlador e operador para canal de comunicação com titulares e ANPD (art. 5º, VIII).

ANPD: Autoridade Nacional de Proteção de Dados.

Tratamento: qualquer operação realizada com dados pessoais (coleta, uso, acesso, transmissão, armazenamento, eliminação etc.).

Anonimização: processo que faz com que o dado não possa mais ser associado a um indivíduo (art. 5º, III, da LGPD).

Cookies: pequenos arquivos gravados no dispositivo do usuário para identificar sessão e preferências.

Usuário: pessoa física maior de 18 anos que utiliza a Plataforma em modalidade direta ao consumidor.

Colaborador: pessoa física vinculada a uma Empresa Contratante, com acesso B2B2C.

Profissional: pessoa física ou jurídica credenciada para teleatendimento.

Empresa Contratante: pessoa jurídica que contrata os serviços da Plumaa em favor de seus colaboradores.

## 2. PAPEL DA PLUMAA NO TRATAMENTO DE DADOS

2.1. A Plumaa atua, conforme o fluxo:

a) como Controladora, em relação aos dados cadastrais de Usuários, Colaboradores e Profissionais, aos dados de uso da Plataforma, aos dados de pagamento e aos dados das respostas aos formulários (quando a Plumaa define finalidades e meios);

b) como Operadora, em relação a certos fluxos, notadamente quando uma Empresa Contratante fornece a lista inicial de Colaboradores e define a operação de aplicação de formulários em caráter corporativo;

c) em controladoria conjunta com a Empresa Contratante, em hipóteses específicas de decisão conjunta sobre meios e finalidades.

2.2. O Profissional da Saúde atua como Controlador independente em relação aos dados clínicos produzidos no atendimento (anamnese, prontuário, hipóteses, condutas), observando as normas éticas de sua profissão.

## 3. DADOS COLETADOS E FINALIDADES

### 3.1. Usuário Comum (B2C)

| Categoria | Exemplos | Finalidade | Base Legal |
|-----------|----------|-----------|-----------|
| Cadastrais | Nome completo, e-mail, CPF, data de nascimento, telefone, senha (hashed) | Criação de conta, autenticação, comunicações, emissão fiscal, comprovação de maioridade | Execução de contrato (art. 7º, V); cumprimento de obrigação legal (art. 7º, II) |
| Uso da Plataforma | Histórico de agendamentos, profissionais visualizados, logs de acesso, IP, dispositivo | Prestação do serviço, suporte, segurança, evolução do produto | Execução de contrato; legítimo interesse (art. 7º, IX) |
| Pagamento | Dados parciais de cartão, meio de pagamento, valor, status | Processamento financeiro | Execução de contrato |
| Saúde (sensíveis) | Informações passadas ao Profissional em sessão (prontuário) | Prestação do atendimento pelo Profissional | Tutela da saúde, por profissional (art. 11, II, "f") — Controlador: Profissional |
| Comunicação | Conteúdo de mensagens com suporte | Atendimento | Execução de contrato |

### 3.2. Colaborador (B2B2C)

| Categoria | Exemplos | Finalidade | Base Legal |
|-----------|----------|-----------|-----------|
| Cadastrais mínimos | Nome, e-mail, setor, cargo | Convite e acesso, aplicação de formulários, comunicação | Controladora: Empresa (execução do contrato de trabalho, cumprimento da NR-01, legítimo interesse); Operadora/Controladora: Plumaa |
| Respostas a Formulários (sensíveis — saúde) | COPSOQ, CBI, outros | Avaliação individual (para o próprio Colaborador), geração de dashboards agregados corporativos, apoio à NR-01, evolução da Plataforma | Consentimento específico (art. 11, I); tutela da saúde (art. 11, II, "f"); proteção da vida (art. 11, II, "e", quando aplicável) |
| Cadastrais adicionais (opcionais) | Telefone, data de nascimento, CPF (se Plano B2B2C) | Adesão ao Plano B2B2C, faturamento, comprovação de maioridade | Execução de contrato |
| Uso da Plataforma | Histórico de acessos, métricas | Operação e evolução | Execução de contrato; legítimo interesse |
| Dados clínicos em teleconsulta | Anamnese e prontuário | Atendimento | Tutela da saúde (art. 11, II, "f") — Controlador: Profissional |

### 3.3. Profissional da Saúde

| Categoria | Exemplos | Finalidade | Base Legal |
|-----------|----------|-----------|-----------|
| Cadastrais | Razão social, CNPJ, nome do RT, CPF, e-mail, celular, endereço, dados bancários | Credenciamento, repasses, emissão fiscal | Execução de contrato; cumprimento de obrigação legal |
| Profissionais | Inscrição no conselho, formação, especialização, currículo | Verificação e publicação de perfil | Execução de contrato |
| Integração Google | E-mail Google, agenda, links Meet | Funcionamento operacional (OAuth) | Execução de contrato; consentimento |
| Uso da Plataforma | Agenda, atendimentos, avaliações | Operação da Plataforma | Execução de contrato; legítimo interesse |

### 3.4. Empresa Contratante

| Categoria | Exemplos | Finalidade | Base Legal |
|-----------|----------|-----------|-----------|
| Cadastrais | Razão social, CNPJ, endereço, representantes, contatos | Execução do contrato B2B, faturamento, comunicação | Execução de contrato |
| Lista de Colaboradores (compartilhada pela Empresa) | Nome, e-mail, setor | Aplicação dos serviços em favor dos Colaboradores | Relação Controladora (Empresa) ↔ Operadora/Controladora (Plumaa) — ver Cláusula 4 |

## 4. DASHBOARDS CORPORATIVOS — ANONIMIZAÇÃO E LIMITES

4.1. A Plumaa entrega à Empresa Contratante dashboards e relatórios com dados agregados e anonimizados, nos termos do art. 5º, III, da LGPD.

4.2. A Plumaa NÃO entrega à Empresa Contratante:

a) respostas individualizadas a formulários;

b) nome, CPF, e-mail, cargo ou qualquer identificador pessoal de respondentes;

c) informações sobre adesão individual ao Plano B2B2C;

d) conteúdo de teleconsultas, prontuários ou registros clínicos.

4.3. Limiares Estatísticos. Recortes com amostra inferior ao limiar mínimo (tipicamente 5 respondentes, podendo ser elevado conforme risco de reidentificação) são suprimidos ou agrupados, a critério da Plumaa, para impedir identificação por inferência.

4.4. Exceções (art. 7º, IV e VI; art. 11, II, "a", "e" e "f", da LGPD): compartilhamento identificado pode ocorrer excepcionalmente:

a) risco iminente à vida do Colaborador ou de terceiros;

b) ordem judicial ou requisição administrativa legítima;

c) consentimento específico e expresso do Colaborador.

Nessas hipóteses, a Plumaa priorizará o acionamento direto de serviços de emergência (SAMU, CVV, autoridades) quando aplicável, notificando o titular sempre que possível e juridicamente admissível.

## 5. SIGILO DAS TELECONSULTAS

5.1. A Plataforma não grava, não armazena e não monitora o conteúdo das videochamadas (áudio, vídeo ou texto) entre Profissional e paciente.

5.2. A comunicação clínica é operada via Google Meet, integrado por OAuth à conta Google do Profissional.

5.3. O sigilo clínico é dever exclusivo do Profissional, sob o Código de Ética da sua categoria.

5.4. Eventuais gravações realizadas pelo Profissional dependem de autorização expressa do paciente e são de guarda e sigilo exclusivos do Profissional; a Plumaa não tem acesso a essas gravações.

## 6. COMPARTILHAMENTO DE DADOS

6.1. A Plumaa poderá compartilhar dados pessoais com:

a) Profissionais da Saúde escolhidos pelo titular, para a prestação do atendimento (nome, e-mail, informações necessárias à sessão);

b) Processadores de pagamento (Stripe e equivalentes), apenas dados estritamente necessários à transação (a Plumaa não armazena número completo de cartão);

c) Prestadores de infraestrutura (hospedagem em nuvem, e-mail, analytics, comunicação), sob contrato com cláusulas de proteção de dados;

d) Autoridades públicas, em atendimento a ordem judicial, requisição administrativa ou cumprimento de obrigação legal;

e) Empresa Contratante, apenas em formato agregado e anonimizado, conforme Cláusula 4;

f) Sucessores, em caso de fusão, cisão, incorporação ou operações societárias similares, com comunicação aos titulares.

6.2. Não venda de dados. A Plumaa não comercializa dados pessoais com terceiros, nem para fins de marketing de terceiros.

## 7. TRANSFERÊNCIA INTERNACIONAL

7.1. A infraestrutura de nuvem pode estar localizada em servidores nos Estados Unidos ou em outras jurisdições, com rígidos padrões técnicos e contratuais.

7.2. A Plumaa exige dos fornecedores internacionais salvaguardas equivalentes às da LGPD (cláusulas contratuais padrão, certificações de segurança, ou outro mecanismo idôneo), nos termos dos arts. 33 a 36 da LGPD.

## 8. RETENÇÃO E ELIMINAÇÃO

8.1. Os dados pessoais são mantidos pelo tempo necessário ao cumprimento das finalidades, observadas:

a) Logs de acesso: mínimo de 6 (seis) meses (art. 15 do Marco Civil da Internet — para aplicações de internet);

b) Dados cadastrais: durante a vigência da conta e por até 10 (dez) anos após o encerramento, para fins de defesa em eventuais processos (ressalvado período menor determinado por lei ou solicitação de exclusão, quando cabível);

c) Documentos fiscais: prazo legal de guarda tributária (mínimo de 5 anos);

d) Prontuário e registros clínicos: prazo definido pelo conselho profissional aplicável (p. ex., 20 anos para médicos; 5 anos para psicólogos), sob guarda do Profissional, não da Plumaa.

8.2. Dados anonimizados (não pessoais) poderão ser mantidos por prazo indeterminado para fins estatísticos, científicos e de evolução do produto.

8.3. Solicitação de Eliminação. O titular pode solicitar a eliminação de seus dados nos termos do art. 18 da LGPD. A eliminação será realizada, salvo nas hipóteses de guarda obrigatória por lei ou em procedimentos de auditoria, ordem judicial ou exercício regular de direitos.

## 9. DIREITOS DO TITULAR (ART. 18 DA LGPD)

9.1. O titular tem direito a:

a) confirmação da existência de tratamento;

b) acesso aos dados;

c) correção de dados incompletos, inexatos ou desatualizados;

d) anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade;

e) portabilidade a outro fornecedor, nos termos da regulamentação;

f) eliminação dos dados tratados com base no consentimento (ressalvadas exceções legais);

g) informação sobre entidades com as quais houve compartilhamento;

h) informação sobre a possibilidade de não fornecer consentimento e suas consequências;

i) revogação do consentimento a qualquer tempo;

j) oposição ao tratamento realizado com fundamento em outra base legal que não o consentimento, em caso de descumprimento da LGPD;

k) revisão de decisões automatizadas (quando aplicável — a Plumaa, atualmente, não adota decisões automatizadas que produzam efeitos jurídicos ou impactem significativamente os titulares).

9.2. Como exercer: basta entrar em contato pelos canais oficiais (Cláusula 13). A Plumaa responderá nos prazos e nos termos da LGPD.

9.3. Dados Clínicos (Prontuário). O exercício de direitos relacionados a prontuário e registros clínicos deve ser dirigido ao Profissional da Saúde correspondente.

## 10. SEGURANÇA DA INFORMAÇÃO

10.1. A Plumaa adota medidas técnicas e administrativas compatíveis com o risco e com a natureza dos dados, incluindo:

- Criptografia em trânsito (TLS) e, conforme o caso, em repouso;
- Hashing de senhas;
- Controle de acesso por perfil, com princípio do menor privilégio;
- Logs de acesso e auditoria;
- Backups regulares;
- Monitoramento de incidentes;
- Cláusulas de proteção contratual com fornecedores.

10.2. Incidentes de Segurança. Em caso de incidente com risco relevante aos titulares, a Plumaa comunicará à ANPD e aos titulares, nos prazos razoáveis e nos termos do art. 48 da LGPD.

10.3. Co-Responsabilidade do Titular. Cabe ao titular manter sigilo sobre sua senha, não compartilhá-la com terceiros, utilizar dispositivos seguros e reportar imediatamente à Plumaa qualquer suspeita de acesso indevido.

## 11. COOKIES E TECNOLOGIAS SIMILARES

11.1. A Plataforma utiliza cookies estritamente necessários (p. ex., sessão, autenticação), analíticos (p. ex., desempenho, métricas) e, eventualmente, funcionais e de preferência. A Plumaa não utiliza cookies publicitários de terceiros para rastreamento de perfil comportamental com finalidade de propaganda segmentada.

11.2. O titular pode configurar seu navegador para restringir cookies, ciente de que algumas funcionalidades podem ser comprometidas.

## 12. CRIANÇAS E ADOLESCENTES

12.1. A Plataforma destina-se a maiores de 18 anos. A Plumaa não oferece serviços a menores, salvo disposição futura específica, hipótese em que este documento será atualizado com as salvaguardas do art. 14 da LGPD e do ECA.

## 13. CANAIS DE CONTATO E ENCARREGADO (DPO)

13.1. Para quaisquer dúvidas, solicitações de exercício de direitos ou comunicações:

- E-mail: contato@plumaa.com.br
- WhatsApp oficial: [número a ser confirmado]
- Encarregado de Dados (DPO): [Nome a indicar] — contato@plumaa.com.br

13.2. A Plumaa responderá nos prazos razoáveis e nos termos da LGPD. Caso o titular não obtenha resposta satisfatória, poderá recorrer à ANPD (https://www.gov.br/anpd).

## 14. ALTERAÇÕES DESTA POLÍTICA

14.1. Esta Política poderá ser atualizada a qualquer tempo, com comunicação por e-mail ou aviso ostensivo na Plataforma em caso de alterações substanciais.

14.2. A versão vigente é a publicada nesta página, com a data de última atualização indicada no cabeçalho.

## 15. LEI APLICÁVEL E FORO

15.1. Esta Política é regida pelas leis da República Federativa do Brasil.

15.2. Fica eleito o Foro da Comarca de São Paulo, Estado de São Paulo, como competente para dirimir quaisquer controvérsias, ressalvadas as hipóteses de competência absoluta em favor do consumidor (CDC).

ARAUJO & SILVA LTDA (nome fantasia: PLUMAA)
CNPJ 65.321.886/0001-10
R. Pais Leme, nº 215, Conj. 1713, Pinheiros, São Paulo/SP, CEP 05.424-150`;

const parseTableRow = (line: string): string[] =>
	line
		.trim()
		.replace(/^\|/, '')
		.replace(/\|$/, '')
		.split('|')
		.map((cell) => cell.trim());

const isTableDivider = (line: string): boolean =>
	/^\|?[\s:-]+(\|[\s:-]+)+\|?$/.test(line.trim());

const renderPolicyContent = (content: string): ReactNode[] => {
	const lines = content.split('\n');
	const nodes: ReactNode[] = [];
	let i = 0;
	let key = 0;

	while (i < lines.length) {
		const line = lines[i];
		const trimmed = line.trim();

		if (!trimmed) {
			i += 1;
			continue;
		}

		if (trimmed.startsWith('### ')) {
			nodes.push(
				<h4
					key={`h4-${key++}`}
					className="pt-4 text-xl font-semibold text-gray-800"
				>
					{trimmed.slice(4)}
				</h4>,
			);
			i += 1;
			continue;
		}

		if (trimmed.startsWith('## ')) {
			nodes.push(
				<h3
					key={`h3-${key++}`}
					className="pt-6 text-2xl font-semibold text-gray-800"
				>
					{trimmed.slice(3)}
				</h3>,
			);
			i += 1;
			continue;
		}

		if (
			trimmed.startsWith('|') &&
			i + 1 < lines.length &&
			isTableDivider(lines[i + 1])
		) {
			const header = parseTableRow(lines[i]);
			i += 2;
			const rows: string[][] = [];

			while (i < lines.length && lines[i].trim().startsWith('|')) {
				rows.push(parseTableRow(lines[i]));
				i += 1;
			}

			nodes.push(
				<div key={`table-wrap-${key++}`} className="overflow-x-auto">
					<table className="w-full min-w-[720px] border-collapse border border-zinc-200 text-left text-sm">
						<thead className="bg-zinc-100">
							<tr>
								{header.map((cell, idx) => (
									<th
										key={`th-${idx}`}
										className="border border-zinc-200 px-3 py-2 font-semibold text-zinc-700"
									>
										{cell}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{rows.map((row, rowIdx) => (
								<tr key={`tr-${rowIdx}`} className="align-top">
									{row.map((cell, cellIdx) => (
										<td
											key={`td-${rowIdx}-${cellIdx}`}
											className="border border-zinc-200 px-3 py-2 text-zinc-600"
										>
											{cell}
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>,
			);
			continue;
		}

		if (trimmed.startsWith('- ')) {
			const items: string[] = [];
			while (i < lines.length && lines[i].trim().startsWith('- ')) {
				items.push(lines[i].trim().slice(2));
				i += 1;
			}
			nodes.push(
				<ul
					key={`ul-${key++}`}
					className="list-inside list-disc space-y-2 pl-4"
				>
					{items.map((item, idx) => (
						<li key={`li-${idx}`}>{item}</li>
					))}
				</ul>,
			);
			continue;
		}

		const paragraphLines: string[] = [];
		while (i < lines.length) {
			const current = lines[i].trim();
			if (
				!current ||
				current.startsWith('## ') ||
				current.startsWith('### ') ||
				(current.startsWith('|') &&
					i + 1 < lines.length &&
					isTableDivider(lines[i + 1])) ||
				current.startsWith('- ')
			) {
				break;
			}
			paragraphLines.push(lines[i]);
			i += 1;
		}

		nodes.push(
			<p key={`p-${key++}`} className="whitespace-pre-line">
				{paragraphLines.join('\n').trim()}
			</p>,
		);
	}

	return nodes;
};

const PrivacyPolicyPage = () => {
	return (
		<main className="mx-auto w-full max-w-3xl px-4 py-12 md:py-20">
			{/* SEO Configurado */}
			<Helmet>
				<title>Diretrizes de Privacidade e Dados | Plumaa</title>
				<meta
					name="description"
					content="Conheça as normas de privacidade da Plumaa. Detalhamos como protegemos suas informações e asseguramos a confidencialidade total das teleconsultas."
				/>

				<meta property="og:type" content="website" />
				<meta
					property="og:title"
					content="Diretrizes de Privacidade e Dados | Plumaa"
				/>
				<meta
					property="og:description"
					content="Profissionais de saúde mental qualificados. Agendamento digital seguro."
				/>

				<meta
					property="og:image"
					content="https://res.cloudinary.com/dd5pbp2tb/image/upload/v1765924080/Banner_rxsb8s.png"
				/>
				<meta
					property="og:image:secure_url"
					content="https://res.cloudinary.com/dd5pbp2tb/image/upload/v1765924080/Banner_rxsb8s.png"
				/>

				<meta property="og:image:type" content="image/png" />
				<meta property="og:image:width" content="1200" />
				<meta property="og:image:height" content="630" />
			</Helmet>

			<div className="mb-12 text-center">
				<h1 className="text-3xl font-bold text-gray-900 md:text-4xl">
					POLÍTICA DE PRIVACIDADE — PLUMAA
				</h1>
				<h2 className="mt-2 text-xl text-zinc-600 md:text-2xl">
					ARAUJO & SILVA LTDA — Nome Fantasia: PLUMAA
				</h2>
				<p className="mt-6 font-semibold text-gray-800">
					Última atualização: 22 de abril de 2026
				</p>
			</div>
			<div className="space-y-6 text-justify text-zinc-600">
				{renderPolicyContent(PRIVACY_POLICY_TEXT).map((node, idx) => (
					<Fragment key={`node-${idx}`}>{node}</Fragment>
				))}
			</div>
		</main>
	);
};

export default PrivacyPolicyPage;
